// Helpers for Ops Alert notifications (TC-2018).
//
// Every alert on the Ops Alerts tab gets an identity. A notification is only
// raised for an identity not seen before, so an alert that stays on the page is
// announced once, and one that clears and later returns is announced again.
// Viewing or dismissing a notification never removes the alert itself: alerts
// leave the page only when the scheduled update drops them from the snapshot.

const SEEN_STORAGE_KEY = "opsAlertsSeen";
const MAX_NAMED_PER_TYPE = 3;

const isRow = (row) => row && typeof row === "object" && !Array.isArray(row);
const rowsOf = (rows) => (Array.isArray(rows) ? rows.filter(isRow) : []);

// Identity of a generic alert row: every field except the position size, which
// changes during the day without making it a different alert.
const rowKey = (row) =>
  JSON.stringify(
    Object.keys(row)
      .filter((field) => field !== "Amount")
      .sort()
      .map((field) => [field, row[field]])
  );

const genericAlerts = (rows) => rowsOf(rows).map((row) => ({ key: rowKey(row) }));

// Mirrors the page's own count: one alert per short position group with no comment.
const shortTagAlerts = (rows) => {
  const groups = new Map();
  rowsOf(rows).forEach((row) => {
    const key = [
      row.StrategyType,
      row.Strategy1,
      row.Symbol,
      row.Alias2,
      row.SecType,
      row.isHedge,
    ].join("|");
    if (!groups.has(key)) groups.set(key, row.Comment);
  });
  return [...groups]
    .filter(([, comment]) => !comment)
    .map(([key]) => ({ key }));
};

const convertArbAlerts = (rows) =>
  rowsOf(rows)
    .filter((row) => row.StrategyName && row.AlertStatus)
    .map((row) => ({
      key: `${row.StrategyName}|${row.AlertStatus}`,
      text: `${row.StrategyName}: ${row.AlertStatus.replace(/^Alert:\s*/, "")}`,
    }));

// The same alert types the Ops Alerts tab counts. Stock ratio check is left out
// because the page does not count it as an alert either.
export const ALERT_TYPES = [
  { id: "banks_in_GIP", label: "Banks in GIP", build: genericAlerts },
  { id: "144A_in_GIP", label: "144A in GIP", build: genericAlerts },
  { id: "REG_S_in_GEF", label: "Reg S in GEF", build: genericAlerts },
  {
    id: "short_tags",
    label: "Short positions missing a comment",
    build: shortTagAlerts,
  },
  { id: "LP_positions", label: "LP positions", build: genericAlerts },
  { id: "convert_arb_positions", label: "Convert Arb", build: convertArbAlerts },
];

export const OPS_TYPE_IDS = ALERT_TYPES.map((type) => type.id);
export { PORTFOLIO_ALERT_TYPE } from "./portfolioThresholds";

// Every current alert as { type, label, key, text? }, `key` unique across types.
export const collectAlerts = (data) => {
  if (!isRow(data)) return [];
  const alerts = [];
  const seenKeys = new Set();
  ALERT_TYPES.forEach(({ id, label, build }) => {
    build(data[id]).forEach((alert) => {
      const key = `${id}|${alert.key}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      alerts.push({ type: id, label, key, text: alert.text });
    });
  });
  return alerts;
};

export const findNewAlerts = (alerts, seenKeys) =>
  alerts.filter((alert) => !seenKeys.has(alert.key));

// The seen set after an evaluation: current alerts of the sources that were
// ready, plus what was remembered for sources that were not ready yet. Without
// the second part, a source that loads late would have its remembered alerts
// dropped and announced again on every page load.
export const nextSeenKeys = (previousSeen, currentAlerts, readyTypes) => {
  const next = new Set(currentAlerts.map((alert) => alert.key));
  previousSeen.forEach((key) => {
    if (!readyTypes.includes(key.split("|")[0])) next.add(key);
  });
  return next;
};

export const loadSeenKeys = () => {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

export const saveSeenKeys = (keys) => {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // Storage unavailable: notifications may repeat after a reload, nothing else breaks.
  }
};

// One line per alert type. Types with a few named alerts list them; otherwise a count.
// `noun` names where the alerts are shown ("Ops", "Portfolio") for the title.
export const buildMessage = (newAlerts, noun = "") => {
  const count = newAlerts.length;
  const title = `${count} new ${noun ? `${noun} ` : ""}alert${count === 1 ? "" : "s"}`;
  const byType = new Map();
  newAlerts.forEach((alert) => {
    if (!byType.has(alert.type)) byType.set(alert.type, []);
    byType.get(alert.type).push(alert);
  });
  const lines = [];
  byType.forEach((alerts) => {
    const named = alerts.every((alert) => alert.text);
    if (named && alerts.length <= MAX_NAMED_PER_TYPE) {
      alerts.forEach((alert) => lines.push(`${alert.label} - ${alert.text}`));
    } else {
      lines.push(`${alerts[0].label}: ${alerts.length} new`);
    }
  });
  return { title, body: lines.join("\n") };
};
